import { localDate, packageLastInstant, type OwnerParticipant, type ProfileVisit, type UpcomingBooking } from "@teamna/shared";
import { useState } from "react";
import {
  ArrowRightIcon,
  Avatar,
  Badge,
  Button,
  CalendarIcon,
  ChipRow,
  DataTable,
  ErrorState,
  MinusCircleIcon,
  PauseCircleIcon,
  PlusCircleIcon,
  Sheet,
  SkeletonList,
} from "../../components/index.ts";
import { useActiveBusiness, useParticipantProfile } from "@teamna/web-client/hooks/use-owner.ts";
import { formatDate, formatDateTime, formatMoney } from "@teamna/web-client/lib/format.ts";
import type { Words } from "../families/model.ts";
import { OwnerParticipantPhoto } from "../participant-photo.tsx";
import { describeWeekdays } from "../setup/frame.tsx";
import { EndManyPausesPanel, EndPausePanel, FreezeManyPanel, FreezePanel, PackagePauseNotice } from "./freeze.tsx";
import { CarryOverPanel, ExtendPanel } from "./package-validity.tsx";
import { HealthDetails } from "./health.tsx";
import { OwnerMembership } from "./membership.tsx";
import { AdjustPanel, BookPanel, CancelPanel, ledgerColumns } from "./panels.tsx";
import { RecordPaymentButton, RecordPaymentPanel } from "./record-payment.tsx";

/** How many bookings the Overview card lists. The Sessions tab lists them all. */
const NEXT_ON_CARD = 3;

/**
 * One child on the Overview tab: who they are and where (group, staff, usual days, photo), the
 * balance as a big number with the package and paid or owed, the actions that change the balance,
 * and the next bookings.
 */
export function ChildCard({
  businessId,
  participant,
  memberName,
  timezone,
  words,
}: {
  businessId: string;
  participant: OwnerParticipant;
  memberName: string;
  timezone: string;
  words: Words;
}) {
  const profile = useParticipantProfile(businessId, participant.id);
  // The front desk (T1) records payments and books here; the photo, the balance and a pause are the owner's.
  const { may } = useActiveBusiness();
  const [adjust, setAdjust] = useState<"add" | "remove" | null>(null);
  const [recording, setRecording] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [cancel, setCancel] = useState<UpcomingBooking | null>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [endPause, setEndPause] = useState(false);
  // The Freeze of this child and its end (D17): the packs bought for this child.
  const [freezeChild, setFreezeChild] = useState(false);
  const [endChild, setEndChild] = useState(false);
  // Extend and Carry over (card #1): the packs of this child, and the packs of the family.
  const [extendOpen, setExtendOpen] = useState(false);
  const [carryOpen, setCarryOpen] = useState(false);

  const balances = profile.data?.balances ?? [];
  const main = balances.find((row) => row.packageKind === "group") ?? balances[0] ?? null;
  // The family's membership (M2): its month in force counts with the packages (#ai-feedback
  // 2026-10-30, a swimmer with only a membership showed "No package" and 0).
  const membership = profile.data?.membership ?? null;
  // `periodEndsAt` is set only for a paid month in force; its start can be null (an older month).
  const month = membership?.periodEndsAt ? membership : null;
  const monthLeft = month && month.cap !== null ? Math.max(0, month.cap - month.used) : 0;
  const sessionsLeft = profile.data ? balances.reduce((total, row) => total + row.sessionsLeft, 0) + monthLeft : participant.sessionsLeft;
  const noLimit = profile.data ? month?.unlimited === true : participant.unlimited === true;
  const name = participant.displayName;
  // The list row already knows these, so the card does not say "No package" while the profile loads.
  const packageName = profile.data ? (main?.packageName ?? membership?.name ?? null) : participant.packageName;
  const paymentStatus = profile.data
    ? main?.packageName
      ? main.paymentStatus
      : month
        ? "paid"
        : membership && profile.data.participant.paymentStatus === "owed"
          ? // The month of today waits for its payment (review F6.1), also when the membership is
            // cancelling (review F8.1). The profile's own row decides, never the list's older copy
            // (review F9.1): a month that ended is not owed (F7.1).
            "owed"
          : null
    : participant.paymentStatus;
  const upcoming = profile.data?.upcoming ?? [];
  // The packs bought for this child (D17): a child can be sick while a sibling is not, so the Freeze
  // here pauses this child's packs only. Freeze all on the page pauses every child.
  const ownPacks = (profile.data?.packs ?? []).filter((pack) => pack.participantId === participant.id && pack.state !== "ended");
  const ownFreezable = ownPacks.filter((pack) => pack.canFreeze && !pack.pause);
  const ownPause = ownPacks.find((pack) => pack.pause)?.pause ?? null;
  // A child with packs of their own shows only their own pause, never a sibling's.
  const pause = ownPacks.length > 0 ? ownPause : (main?.pause ?? null);
  // A child with no pack of their own (a pack bought for the family): Freeze pauses the newest
  // package of this kind that still has sessions, as before (migration 0071).
  const freezable = ownPacks.length === 0 ? (main?.freezable ?? null) : null;
  const canFreeze = freezable !== null;
  const childPacks = (profile.data?.packs ?? []).filter((pack) => pack.participantId === participant.id || !pack.participantId);
  const extendable = childPacks.filter((pack) => pack.canExtend);
  const carryable = childPacks.filter((pack) => pack.canCarryOver);

  return (
    <section aria-label={name} className="rounded-card border border-line bg-paper shadow-card">
      <div className="flex flex-wrap items-center gap-4 border-b border-line px-5 py-4">
        <Avatar name={name} photoUrl={participant.photoUrl} size={56} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="text-[21px]" dir="auto">
            {name}
          </h2>
          <p className="text-[14px] text-ink-2">
            {participant.groupName ?? `Not placed in a ${words.group}`}
            {participant.staffName ? ` with ${participant.staffName}` : ""}. {describeWeekdays(participant.usualWeekdays)}.
          </p>
        </div>
        {may("setPhoto") ? (
          <Button variant="secondary" onClick={() => setPhotoOpen(true)}>
            {participant.photoUrl ? "Change photo" : "Add a photo"}
          </Button>
        ) : null}
      </div>
      <Sheet open={photoOpen} onClose={() => setPhotoOpen(false)} title={`Profile photo of ${name}`}>
        <OwnerParticipantPhoto
          businessId={businessId}
          participantId={participant.id}
          name={name}
          photoUrl={participant.photoUrl}
          photosRefused={participant.photosRefused === true}
          onDone={() => setPhotoOpen(false)}
        />
      </Sheet>

      <div className="grid gap-5 px-5 py-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <p className="eyebrow">Sessions left</p>
          {noLimit ? (
            <p className="text-[32px] leading-none font-semibold text-accent-strong">No limit</p>
          ) : (
            <p className="text-[48px] leading-none font-semibold text-accent-strong tabular-nums" aria-label={`${sessionsLeft} sessions left`}>
              {sessionsLeft}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-[14px] text-ink-2">
            <span>{packageName ?? "No package"}</span>
            {paymentStatus === "owed" ? (
              <Badge tone="late">Owed</Badge>
            ) : paymentStatus === "paid" ? (
              <Badge tone="confirmed">Paid</Badge>
            ) : null}
          </div>
          {main?.renewsOn ? (
            <p className="text-[13px] text-ink-3">
              Renews {formatDate(`${main.renewsOn}T12:00:00.000Z`, timezone)}
              {main.priceFils !== null ? ` for ${formatMoney(main.priceFils)}` : ""}.
            </p>
          ) : main?.purchaseId && main.expiresAt ? (
            // A package that does not renew (the opening balance): when its sessions end.
            <p className="text-[13px] text-ink-3">Valid until {formatDate(packageLastInstant(main.expiresAt).toISOString(), timezone)}.</p>
          ) : null}
          {main?.nextPackage ? (
            // A package that waits for this one (#ai-feedback 2026-10-26, card #1).
            <p className="text-[13px] text-ink-3">
              Next: <span dir="auto">{main.nextPackage.packageName}</span>, {main.nextPackage.sessions} {main.nextPackage.sessions === 1 ? "session" : "sessions"}, starts{" "}
              {formatDate(main.nextPackage.startsAt, timezone)}.
            </p>
          ) : null}
          {membership ? (
            <OwnerMembership businessId={businessId} participantId={participant.id} name={name} membership={membership} canCancel={may("cancelMembership")} />
          ) : null}
          {pause ? (
            <PackagePauseNotice
              pause={pause}
              today={localDate(new Date(), timezone)}
              {...(ownPause
                ? may("endManyPauses")
                  ? { onEnd: () => setEndChild(true) }
                  : {}
                : may("endPackageFreeze")
                  ? { onEnd: () => setEndPause(true) }
                  : {})}
            />
          ) : null}
          {/* One column of equal buttons, each with its icon (2026-10-30): money first, then the sessions, then the package's days. */}
          <div className="flex flex-col gap-2 pt-1">
            <RecordPaymentButton fullWidth disabled={!profile.data} refresh={() => profile.refetch()} onClick={() => setRecording(true)} />
            {may("adjustBalance") ? (
              <>
                <Button variant="secondary" fullWidth onClick={() => setAdjust("add")}>
                  <PlusCircleIcon size={18} />
                  Add sessions
                </Button>
                <Button variant="secondary" fullWidth disabled={sessionsLeft === 0} onClick={() => setAdjust("remove")}>
                  <MinusCircleIcon size={18} />
                  Remove sessions
                </Button>
              </>
            ) : null}
            {ownFreezable.length > 0 && may("freezeMany") ? (
              <Button variant="secondary" fullWidth onClick={() => setFreezeChild(true)}>
                <PauseCircleIcon size={18} />
                Freeze
              </Button>
            ) : canFreeze && may("freezePackage") ? (
              <Button variant="secondary" fullWidth onClick={() => setFreezeOpen(true)}>
                <PauseCircleIcon size={18} />
                Freeze
              </Button>
            ) : null}
            {extendable.length > 0 && may("extendPackage") ? (
              <Button variant="secondary" fullWidth onClick={() => setExtendOpen(true)}>
                <CalendarIcon size={18} />
                Extend
              </Button>
            ) : null}
            {carryable.length > 0 && may("carryOverPackage") ? (
              <Button variant="secondary" fullWidth onClick={() => setCarryOpen(true)}>
                <ArrowRightIcon size={18} />
                Carry over
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[16px]">Next sessions</h3>
            <Button variant="secondary" onClick={() => setBookOpen(true)}>
              Book into a session
            </Button>
          </div>
          {profile.isLoading ? (
            <SkeletonList rows={2} />
          ) : profile.error ? (
            <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
          ) : (
            <BookingList bookings={upcoming.slice(0, NEXT_ON_CARD)} timezone={timezone} onCancel={setCancel} />
          )}
          {upcoming.length > NEXT_ON_CARD ? (
            <p className="text-[13px] text-ink-3">
              And {upcoming.length - NEXT_ON_CARD} more. The Sessions tab lists them all.
            </p>
          ) : null}
          {/* The health answers the parent gave for this child (C2). Nothing when they never answered. */}
          <div className="pt-3">
            <HealthDetails businessId={businessId} participantId={participant.id} timezone={timezone} />
          </div>
        </div>
      </div>

      {adjust ? (
        <AdjustPanel
          businessId={businessId}
          participantId={participant.id}
          name={name}
          memberName={memberName}
          direction={adjust}
          current={sessionsLeft}
          onClose={() => setAdjust(null)}
        />
      ) : null}
      {freezeOpen && freezable ? (
        <FreezePanel
          businessId={businessId}
          participantId={participant.id}
          purchaseId={freezable.purchaseId}
          name={name}
          packageName={freezable.packageName}
          expiresAt={freezable.expiresAt}
          timezone={timezone}
          onClose={() => setFreezeOpen(false)}
        />
      ) : null}
      {extendOpen && extendable.length > 0 ? (
        <ExtendPanel businessId={businessId} participantId={participant.id} name={name} packs={extendable} onClose={() => setExtendOpen(false)} />
      ) : null}
      {carryOpen && carryable.length > 0 ? (
        <CarryOverPanel
          businessId={businessId}
          participantId={participant.id}
          name={name}
          packs={carryable}
          timezone={timezone}
          onClose={() => setCarryOpen(false)}
        />
      ) : null}
      {freezeChild ? (
        <FreezeManyPanel
          businessId={businessId}
          participantId={participant.id}
          scope="participant"
          title={ownFreezable.length === 1 ? `Freeze the package of ${name}` : `Freeze the packages of ${name}`}
          name={name}
          packs={ownFreezable}
          timezone={timezone}
          onClose={() => setFreezeChild(false)}
        />
      ) : null}
      {endChild ? (
        <EndManyPausesPanel
          businessId={businessId}
          participantId={participant.id}
          scope="participant"
          title={`End the pause of ${name}?`}
          onClose={() => setEndChild(false)}
        />
      ) : null}
      {endPause && pause ? (
        <EndPausePanel
          businessId={businessId}
          participantId={participant.id}
          pause={pause}
          name={name}
          timezone={timezone}
          onClose={() => setEndPause(false)}
        />
      ) : null}
      {recording ? (
        <RecordPaymentPanel
          businessId={businessId}
          people={[participant]}
          memberName={memberName}
          owed={profile.data?.owed ?? []}
          renewal={profile.data?.membership?.renewal ? { participantId: participant.id, name, membership: profile.data.membership } : null}
          timezone={timezone}
          onClose={() => setRecording(false)}
        />
      ) : null}
      {bookOpen ? (
        <BookPanel
          businessId={businessId}
          participant={participant}
          bookedSessionIds={upcoming.map((row) => row.sessionId)}
          timezone={timezone}
          onClose={() => setBookOpen(false)}
        />
      ) : null}
      {cancel ? (
        <CancelPanel
          businessId={businessId}
          booking={cancel}
          name={name}
          memberName={memberName}
          timezone={timezone}
          onClose={() => setCancel(null)}
        />
      ) : null}
    </section>
  );
}

/** The last sessions the participant came to, newest first. A walk-in without a package shows here too, not in the history. */
export function VisitList({ visits, timezone }: { visits: ProfileVisit[]; timezone: string }) {
  if (visits.length === 0) return <p className="text-[14px] text-ink-3">No visits yet.</p>;
  return (
    <ul className="divide-y divide-line rounded-card border border-line" aria-label="Visits">
      {visits.map((visit) => (
        <li key={visit.bookingId} className="flex flex-col gap-0.5 px-4 py-3">
          <span className="font-semibold text-ink">{formatDateTime(visit.startsAt, timezone)}</span>
          <span className="text-[13px] text-ink-2">
            {visit.groupName}
            {visit.staffName ? ` with ${visit.staffName}` : ""}
            {visit.marked ? ". Marked present." : ". Booked, not marked."}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function BookingList({
  bookings,
  timezone,
  onCancel,
}: {
  bookings: UpcomingBooking[];
  timezone: string;
  onCancel: (booking: UpcomingBooking) => void;
}) {
  if (bookings.length === 0) return <p className="text-[14px] text-ink-3">No upcoming bookings.</p>;
  return (
    <ul className="divide-y divide-line rounded-card border border-line">
      {bookings.map((booking) => (
        <li key={booking.bookingId} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="min-w-0">
            <span className="block font-semibold text-ink">{formatDateTime(booking.startsAt, timezone)}</span>
            <span className="block text-[13px] text-ink-2">
              {booking.groupName}
              {booking.staffName ? ` with ${booking.staffName}` : ""}
            </span>
          </span>
          {/* Secondary here: the panel that opens says what happens and holds the red button. */}
          <Button variant="secondary" onClick={() => onCancel(booking)}>
            Cancel, returns session
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The Sessions tab: for each child, every upcoming booking (with "Book into a session" and the
 * business cancel) and the history of the balance. The member sees every history line, with the
 * reason. With two or more children, chips show one child at a time.
 */
export function ChildSessions({
  businessId,
  participants,
  memberName,
  timezone,
  words,
}: {
  businessId: string;
  participants: OwnerParticipant[];
  memberName: string;
  timezone: string;
  words: Words;
}) {
  const [shown, setShown] = useState<string>("all");
  if (participants.length === 0) {
    return <p className="text-[14px] text-ink-2">No {words.participants} yet, so there are no sessions.</p>;
  }
  const visible = shown === "all" ? participants : participants.filter((participant) => participant.id === shown);
  return (
    <div className="flex flex-col gap-6">
      {participants.length > 1 ? (
        <ChipRow
          options={[{ id: "all", label: `All ${words.participants}` }, ...participants.map((participant) => ({ id: participant.id, label: participant.displayName }))]}
          selectedId={shown}
          onSelect={setShown}
          label="Show the sessions of"
          wrap
        />
      ) : null}
      {visible.map((participant) => (
        <ChildSessionsBlock
          key={participant.id}
          businessId={businessId}
          participant={participant}
          memberName={memberName}
          timezone={timezone}
          titled={participants.length > 1}
        />
      ))}
    </div>
  );
}

function ChildSessionsBlock({
  businessId,
  participant,
  memberName,
  timezone,
  titled,
}: {
  businessId: string;
  participant: OwnerParticipant;
  memberName: string;
  timezone: string;
  titled: boolean;
}) {
  const profile = useParticipantProfile(businessId, participant.id);
  const [bookOpen, setBookOpen] = useState(false);
  const [cancel, setCancel] = useState<UpcomingBooking | null>(null);
  const name = participant.displayName;
  const upcoming = profile.data?.upcoming ?? [];

  return (
    <section aria-label={`Sessions of ${name}`} className="flex flex-col gap-4">
      {titled ? (
        <h2 className="text-[19px]" dir="auto">
          {name}
        </h2>
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[16px]">Coming up</h3>
          <Button variant="secondary" onClick={() => setBookOpen(true)}>
            Book into a session
          </Button>
        </div>
        {profile.isLoading ? (
          <SkeletonList rows={2} />
        ) : profile.error ? (
          <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
        ) : (
          <BookingList bookings={upcoming} timezone={timezone} onCancel={setCancel} />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-[16px]">Visits</h3>
        {profile.isLoading ? <SkeletonList rows={2} /> : profile.error ? null : <VisitList visits={profile.data?.visits ?? []} timezone={timezone} />}
      </div>
      <div>
        <h3 className="text-[16px]">History</h3>
        <p className="mt-1 mb-2 text-[13px] text-ink-3">{memberName} sees every line, with the reason.</p>
        <DataTable
          caption={`History for ${name}`}
          columns={ledgerColumns(timezone)}
          rows={profile.data?.ledger ?? []}
          rowKey={(line) => line.id}
          isLoading={profile.isLoading}
          empty={{ title: "No history yet", message: "Bookings, packages and changes show here." }}
          countLabel={(count) => `${count} ${count === 1 ? "line" : "lines"}`}
          density="compact"
        />
      </div>
      {bookOpen ? (
        <BookPanel
          businessId={businessId}
          participant={participant}
          bookedSessionIds={upcoming.map((row) => row.sessionId)}
          timezone={timezone}
          onClose={() => setBookOpen(false)}
        />
      ) : null}
      {cancel ? (
        <CancelPanel
          businessId={businessId}
          booking={cancel}
          name={name}
          memberName={memberName}
          timezone={timezone}
          onClose={() => setCancel(null)}
        />
      ) : null}
    </section>
  );
}
